import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import ProductAttributes, { ProductAttributesStyles } from "@insite/content-library/Components/ProductAttributes";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { Link } from "@insite/mobius/index";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import getColor from "@insite/mobius/utilities/getColor";
import React, { FC, useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";
import ProductDescription from "@NA/Overrides/Components/ProductDescription";
import ADIColors from "@NA/Utils/ADIColors";
import { getMaxProductHeight }  from "@NA/Utils/Utility";

interface OwnProps {
    showBrand: boolean;
    showTitle: boolean;
    showPartNumbers: boolean;
    showAvailability: boolean;
    showAttributes: boolean;
}

const mapStateToProps = (state: ApplicationState) => ({
    settingsCollection: getSettingsCollection(state),
    view: state.pages.productList.view,
    isAuthenticated: getSession(state).isAuthenticated,
});

type Props = ReturnType<typeof mapStateToProps> & OwnProps & HasProductContext;

export interface ProductListProductInformationStyles {
    container?: GridContainerProps;
    brandStyles?: ProductBrandStyles;
    descriptionStyles?: ProductDescriptionStyles;
    brandGridItem?: GridItemProps;
    descriptionGridItem?: GridItemProps;
    partNumbersGridItem?: GridItemProps;
    partNumbersGridItemAuth?: GridItemProps;
    partNumbersStyles?: ProductPartNumbersStyles;
    availabilityGridItem?: GridItemProps;
    barPading?: TypographyProps;
    availabilityStyles?: ProductAvailabilityStyles;
    attributesGridItem?: GridItemProps;
    attributes?: ProductAttributesStyles;
    searchScoreDataGridItem?: GridItemProps;
    productPartsDetailsGrid?: InjectableCss;
    productPartsDetailsList?: InjectableCss;
    productDetailLink?: LinkPresentationProps;
    descriptionListItem?: GridItemProps;
}

export const productInformationStyles: ProductListProductInformationStyles = {
    container: {
        gap: 10,
        css: css`
            width: 100%;
            > ${GridOffset} {
                ${({ theme }) => { return breakpointMediaQueries(
                    theme, [css` margin-left: 0px; `,],"max",);
                }}
            }
        `,
    },
    productDetailLink: {
        css: css`
            width: 100%;
        `,
        typographyProps: {
            css: css`
                width: 100%;
                ${wrapInContainerStyles}
                display: -webkit-box;
                line-clamp: 3;
                -webkit-line-clamp: 3;
                -webkit-box-orient: vertical;
                overflow: hidden;
                color: ${getColor("text.main")} !important;
                font-size: ${getProp("theme.typography.headerSecondary.size")};
                font-weight: 600;
                ${({ theme }) => { return breakpointMediaQueries(
                    theme, [css` margin-bottom: 10px; `,],"max",); 
                }}
            `,
        },
    },
    brandStyles: {
        nameLink: {
            typographyProps: {
                size: 12,
                weight: 600,
            },
            css: css`
                span {
                    color: ${getColor("text.main")} !important;
                    :hover {
                        text-decoration: underline;
                    }
                }
            `,
        },
        nameText: {
            css: css`
                color: ${getColor("text.main")} !important;
            `,
        },
    },
    descriptionStyles: {
        productDetailLink: {
            css: css`
                width: 100%;
            `,
            typographyProps: {
                size: 14,
            },
        },
    },
    brandGridItem: {
        width: 12,
        css: css`
            padding: 0 0 5px;
            font-weight: 600;
        `,
    },
    descriptionGridItem: {
        width: 12,
        css: css`
            padding: 0;
            line-height: normal;
            //margin-bottom: 5px!important;
            ${({ theme }) => { return breakpointMediaQueries(
                theme, [css` min-height: initial !important; `,],"max",); 
            }}
            
            a span {
                display: -webkit-box;
                line-clamp: 3;
                -webkit-line-clamp: 3;
                -webkit-box-orient: vertical;
                overflow: hidden;
                width: 100%;
                color: ${getColor("text.main")} !important;
                line-height: normal;
                font-weight: 600;
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`width: 85%;margin-bottom: 10px`,
                        css`margin-bottom: 10px`,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )}
                :hover {
                    text-decoration: underline;
                }
            }
        `,
    },
    descriptionListItem: {
        width: 12,
        css: css`
            padding: 0;
            line-height: normal;
            a span {
                font-size: 16px;
                display: -webkit-box;
                line-clamp: 3;
                -webkit-line-clamp: 3;
                -webkit-box-orient: vertical;
                overflow: hidden;
                color: ${getColor("text.main")} !important;
                :hover {
                    text-decoration: underline;
                }
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                                padding-right: 40px;
                            `,
                        null,
                        null,
                        null,
                        null,
                    ],
                    "min",
                )}
            }
        `,
    },
    partNumbersGridItem: {
        width: 12,
        css: css`
            padding: 5px 0;
            line-height: 1;
            display: inline-flex;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css` display: none; `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    partNumbersGridItemAuth: {
        width: 12,
        css: css`
            padding: 0;   
            line-height: 1;
            padding-top: 5px;
            padding-bottom: 5px;
            ${({ theme }) => { return breakpointMediaQueries(
                theme, [null, css` display: none; `,],"max",); 
            }}
        `,
    },
    barPading: {
        css: css`
            padding: 0px 6px;
        `
    },
    availabilityGridItem: {
        width: 12,
        css: css`
           padding: 0;   
        `,
    },
    attributesGridItem: {
        width: 12,
        css: css`
            padding: 0;   
        `,
    },
    searchScoreDataGridItem: {
        width: 12,
        css: css`
            padding: 0 0 5px;
        `,
    },
    productPartsDetailsGrid: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            span {
                margin-right: 5px;
                font-size: 10px;
                color: ${ADIColors.gray4};
                font-weight: 600;
                line-height: normal;
            }
        `,
    },
    productPartsDetailsList: {
        css: css`
            span {
                margin-right: 5px;
                font-size: 12px;
                color: ${ADIColors.gray4};
                font-weight: 600;
            }
        `,
    }
};

const styles = productInformationStyles;

const ProductListProductInformation: FC<Props> = ({
    productContext,
    settingsCollection,
    showBrand,
    showTitle,
    showPartNumbers,
    showAvailability,
    showAttributes,
    view,
    isAuthenticated,
}) => {
    const { product, productInfo } = productContext;
    const pricing = productInfo?.pricing || product.pricing;

    if (!product) {
        return null;
    }

    const showAuthorizationRequired =
        pricing &&
            pricing.additionalResults &&
            pricing.additionalResults.isVisible &&
            pricing.additionalResults.isVisible === "N"
            ? "true"
            : "false";

            const [minDescHeight, setMinDescHeight] = useState(15);
            const [minPartHeight, setMinPartHeight] = useState(14);
            useEffect(() => {
                const timeoutId = setTimeout(() => {
                    const maxDescHeight = getMaxProductHeight('[data-test-selector="plpProductDescriptionLink"]');
                    const maxPartHeight = getMaxProductHeight('[data-test-selector="plpPartNumberGrid"]');
                    setMinDescHeight(maxDescHeight);
                    setMinPartHeight(maxPartHeight);
                }, 100);
           
                return () => clearTimeout(timeoutId);
            }, [productContext]);

    return (
        <GridContainer {...styles.container}>
            {product.brand && showBrand && (
                <GridItem {...styles.brandGridItem}>
                    <ProductBrand brand={product.brand} extendedStyles={styles.brandStyles} />
                </GridItem>
            )}
            {showTitle && (
                <GridItem {...(view === "Grid" ? styles.descriptionGridItem : styles.descriptionListItem)} data-test-selector="plpProductDescriptionLink" style={view === "Grid" ? { minHeight: minDescHeight + 'px' } : {}}>
                    <ProductDescription
                        product={productContext}
                        extendedStyles={styles.descriptionStyles}
                        sourceName="Product List"
                    />
                </GridItem>
            )}
            {showPartNumbers && (
                <GridItem {...(isAuthenticated ? styles.partNumbersGridItem : styles.partNumbersGridItemAuth)} data-test-selector="plpPartNumberGrid" style={view === "Grid" ? { minHeight: minPartHeight + 'px' } : {}}>
                    <StyledWrapper {...(view === "Grid" ? styles.productPartsDetailsGrid : styles.productPartsDetailsList)}>
                        <Typography as="span">{product.detail?.modelNumber && product.detail?.modelNumber !=="" ? product.detail?.modelNumber : product.manufacturerItem}</Typography>
                        <Typography {...styles.barPading} as="span">|</Typography>
                        <Typography as="span">{product.productNumber}</Typography>
                    </StyledWrapper>
                </GridItem>
            )}
            {settingsCollection.productSettings.showInventoryAvailability &&
                showAvailability &&
                isAuthenticated &&
                showAuthorizationRequired !== "true" && (
                    <GridItem {...styles.availabilityGridItem}>
                        <ProductContextAvailability extendedStyles={styles.availabilityStyles} />
                    </GridItem>
                )}
            {showAttributes && (
                <GridItem {...styles.attributesGridItem}>
                    <ProductAttributes maximumNumberAttributeTypes={3} extendedStyles={styles.attributes} />
                </GridItem>
            )}
        </GridContainer>
    );
};

export default connect(mapStateToProps)(withProductContext(ProductListProductInformation));
